import React from 'react';
import { Redirect } from 'react-router-dom';
import { Subtract } from 'utility-types';

import { CommonErrorCode } from '~commonInfra/utils/ErrorCodes';
import { Filter } from '~commonInfra/utils/Filter';
import { RequestAccessError } from '~src/requestAccess/components/RequestAccessError';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';

import { RouterError } from '../../bootstrap/routerHelpers/RouterError';
import { Projects } from '../../projects/resources/Projects';
import { INamedItemInfo } from '../../spacesPlatform/models/resourceInfoParts';
import { FlowDetailInfo, FlowListItemInfo } from '../models/FlowInfo';
import { Flows } from '../resources/Flows';
import { FlowAllowedActions } from '../serverApi/types/FlowAllowedActions';

interface InjectedFlowSpaceData {
  readonly flowAllowedActions: FlowAllowedActions;
  readonly flowDetailInfo: FlowDetailInfo;
  readonly flowListItemInfo: FlowListItemInfo;
  readonly projectHierarchy: INamedItemInfo[];
}

export interface InjectedFlowSpaceDataFetcherProps extends InjectedFlowSpaceData {
  readonly refreshData: () => void;
}

export interface FlowSpaceDataFetcherProps {
  readonly siteName: string;
  readonly flowId: string;
  readonly siteUrls: SpacesSiteUrls;
  readonly isRequestAccessEnabled: boolean;
  readonly grantAccessDisplay: boolean;
  readonly grantAccessUrl: string;
}

export function withFlowSpaceDataFetcher<P extends InjectedFlowSpaceDataFetcherProps>(
  Component: React.ComponentType<P>
) {
  return function FlowSpaceDataFetcher(
    props: Subtract<P, InjectedFlowSpaceDataFetcherProps> & FlowSpaceDataFetcherProps
  ) {
    const [ error, setError ] = React.useState(false);
    const [ injectedData, setInjectedData ] = React.useState<InjectedFlowSpaceData>(undefined);
    const [ errorCode, setErrorCode ] = React.useState(null);

    let didCancel = false;

    const refreshData = () => {
      fetchFlowSpaceData(props.flowId)
        .then(injectedData => {
          !didCancel && setError(false);
          !didCancel && setInjectedData(injectedData);
        })
        .catch((e) => {
          !didCancel && setError(true);
          !didCancel && setErrorCode(e);
        });
    };

    React.useEffect(() => {
      refreshData();
      // This noncompliance existed before we enabled the lint rule. If you’re here, please consider fixing it!
      // eslint-disable-next-line react-hooks/exhaustive-deps
      return () => didCancel = true;
    }, [ props.flowId , props.siteName]);

    if (error) {
      if(props.isRequestAccessEnabled) {
        if (errorCode === CommonErrorCode.CallingUserInsufficientPermissions) {
          return <Redirect to={`${props.siteUrls.homePath()}${props.siteUrls.flowRequestAccessPath(props.flowId)}`} />;
        } else {
          return <RequestAccessError url={props.siteUrls.exploreUrl()} contentIdentifier={props.flowId} contentType={'flow'} errorCode={errorCode}/>;
        }
      }
      return <RouterError errorType={'flow'} urls={props.siteUrls}/>;
    } else if (!injectedData) {
      return null;
    }

    return <Component {...props as unknown as P} {...injectedData} refreshData={refreshData}/>;
  };
}

async function fetchFlowSpaceData(flowId: string): Promise<InjectedFlowSpaceData> {
  const flowAllowedActions = await Flows.actions(flowId);
  const flowDetailInfo = await Flows.getById(flowId);
  const flowListItemInfo = await Flows.all()
    .withFilters([Filter.fieldEq('id', flowId)])
    .getFirst();
  let projectHierarchy: INamedItemInfo[] = [];

  // TODO:- Temporary fix for breadcrumbs will be updated in TFS 1210037
  if (flowDetailInfo.project) {
    projectHierarchy = await Projects.getProjectHierarchy(flowDetailInfo.project.id);
  }

  return {
    flowAllowedActions,
    flowDetailInfo,
    flowListItemInfo,
    projectHierarchy,
  };
}
